// Agent modes (plan 101 slice 06) — per org, with a per-repo override, switch a
// box mode (scout / worker / reviewer / devops) on or off and NARROW the `code.*`
// tools it may offer the model. The rules (the ceiling a list may only narrow,
// the floor it must keep) are the domain's (`entities/agent-mode.ts`) and travel
// ON the wire with each mode, so this bundle renders the checklist the server
// resolved rather than its own copy of the table. The API validates every write
// and its 422 `detail` is what the row shows (`apps/dashboard/CLAUDE.md` — the
// server is the arbiter).
//
// THE CONTRACT (`apps/api/src/routes/agent-modes.ts`), one seam per verb:
//   GET    /v1/agent-modes                    → { data: AgentModeView[], next_cursor: null }
//   PUT    /v1/agent-modes         body { mode_key, enabled, code_tools } → { mode }
//   DELETE /v1/agent-modes/:modeKey           → 204 (drop the org row)
// and the same three under /v1/agent-modes/repo/:repoId (the repositories UUID;
// not /v1/repos/:id/…, which `GET /v1/repos/:owner/:repo` would swallow). A view
// is the resolved mode (repo > org > default) with the `source` and `revision`
// that won.

import { type AgentModeKey, AgentModeKeySchema, AgentModeSourceSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** Which rows a panel reads and writes: the account's, or one repo's override (by UUID). */
export type AgentModesScope =
  | { readonly kind: 'org' }
  | { readonly kind: 'repo'; readonly repoId: string };

/**
 * One resolved mode. NON-strict, per `api-errors.ts`' `parseBody` rule: the
 * server gaining a field must not break a bundle already in a browser.
 * `code_tools` is `string[]`, not the tool enum — a name the server knows and
 * this bundle does not is not a reason to blank the panel.
 */
export const AgentModeViewSchema = z.object({
  key: AgentModeKeySchema,
  enabled: z.boolean(),
  revision: z.number().int().nonnegative(),
  source: AgentModeSourceSchema,
  code_tools: z.array(z.string()).nullable(),
  /** What `code_tools: null` means for this mode; any list must be a subset. */
  ceiling: z.array(z.string()),
  /** The tools every run of this mode needs — always on. */
  floor: z.array(z.string()),
  dropped_tools: z.array(z.string()),
  /**
   * Present when the WINNING stored row no longer passes the domain's rules (it
   * bypassed the write seam, or a ceiling moved under it). The server names the
   * reason; the row offers a reset. Optional: an older API never sends it.
   */
  invalid: z.object({ reason: z.string() }).nullish(),
});
export type AgentModeView = z.infer<typeof AgentModeViewSchema>;

const AgentModeListSchema = z.object({ data: z.array(AgentModeViewSchema) });

export const AGENT_MODES_QUERY_KEY = 'agent_modes';

/** The collection path for a scope — the ONE place either address is spelled. */
export function agentModesPath(scope: AgentModesScope): string {
  return scope.kind === 'org'
    ? '/v1/agent-modes'
    : `/v1/agent-modes/repo/${encodeURIComponent(scope.repoId)}`;
}

/**
 * The resolved modes for a scope. Idle with no session, like every settings
 * read. `enabled` is a second, REACTIVE gate (default admits): every GET here
 * needs `write:prompts` (`apps/api/src/routes/agent-modes.ts`), so a caller
 * that already knows it lacks the scope passes `() => false` and the query
 * never fires — never a guaranteed 403 sent just to be caught. Read inside
 * the options callback (not a captured value) so it tracks a signal that
 * settles after mount (`canEditModes`, resolved once `/v1/me` answers).
 */
export function useAgentModes(
  scope: () => AgentModesScope,
  token: string | null = SESSION_TOKEN,
  enabled: () => boolean = () => true,
) {
  return createQuery<readonly AgentModeView[]>(() => ({
    queryKey: [AGENT_MODES_QUERY_KEY, agentModesPath(scope()), token, activeAccountId()],
    enabled: token !== null && enabled(),
    queryFn: async () =>
      (await apiFetch(agentModesPath(scope()), token, undefined, { schema: AgentModeListSchema }))
        .data,
  }));
}

/**
 * A mode write's precondition (#5208, `SetAgentModeBodySchema.expect`): the
 * `revision` this caller read, or `{absent: true}` when it read no override at
 * this scope. Omitted = the unconditional upsert.
 */
export type AgentModeExpect = { readonly revision: number } | { readonly absent: true };

/**
 * The PUT body — the domain's `AgentModeRow` shape, with `code_tools` widened to
 * `string[]`: writing a mode back re-sends the list the server resolved, which
 * may name a tool this bundle predates. The server's schema is the check.
 */
export type AgentModeWrite = {
  readonly mode_key: AgentModeKey;
  readonly enabled: boolean;
  readonly code_tools: readonly string[] | null;
  /** #5208 precondition; omitted keeps every existing caller's unconditional upsert. */
  readonly expect?: AgentModeExpect;
};

/**
 * Write this scope's row for one mode, answering the resolved view it just wrote
 * (`revision` included — the drawer's Undo (`agent-mode-save.ts`) guards its
 * replay with it, the same #5208 idiom as a route pin's PUT echo). The rest of
 * the view is not re-derived from it: every OTHER reader still re-reads.
 */
export const putAgentMode = async (
  scope: AgentModesScope,
  row: AgentModeWrite,
  token: string | null = SESSION_TOKEN,
): Promise<AgentModeView> => {
  const { mode } = await apiFetch<{ mode: AgentModeView }>(agentModesPath(scope), token, {
    method: 'PUT',
    body: JSON.stringify(row),
  });
  return mode;
};

/** Drop this scope's row, so the mode inherits again (repo → org → default).
 * `expectRevision` refuses the reset 409 `config-stale` when the override moved
 * since it was read; omitted = the unconditional reset. */
export const resetAgentMode = (
  scope: AgentModesScope,
  key: AgentModeKey,
  token: string | null = SESSION_TOKEN,
  expectRevision?: number,
): Promise<void> => {
  const query = expectRevision === undefined ? '' : `?expect_revision=${expectRevision}`;
  return apiFetch<void>(`${agentModesPath(scope)}/${encodeURIComponent(key)}${query}`, token, {
    method: 'DELETE',
  });
};

/** One checkbox of a mode's tool list. `floor` = required by every run, so locked on. */
export type ToolChoice = {
  readonly tool: string;
  readonly checked: boolean;
  readonly floor: boolean;
};

/** The mode's ceiling as a checklist: `null` code_tools checks every tool. */
export function toolChoices(view: AgentModeView): ToolChoice[] {
  return view.ceiling.map((tool) => ({
    tool,
    checked: view.code_tools === null || view.code_tools.includes(tool),
    floor: view.floor.includes(tool),
  }));
}

/**
 * The `code_tools` to write after one checkbox flips, in ceiling order. The
 * whole ceiling checked is `null`, not the full list: `null` means "no
 * narrowing", and a full list written out would silently leave behind a tool
 * added to the ceiling later.
 */
export function withTool(view: AgentModeView, tool: string, on: boolean): string[] | null {
  const next = toolChoices(view)
    .filter((c) => (c.tool === tool ? on : c.checked))
    .map((c) => c.tool);
  return next.length === view.ceiling.length ? null : next;
}

/**
 * Whether THIS scope owns the winning row, so a DELETE changes something. A repo
 * panel showing the org's row inherits it; deleting is the org page's act.
 */
export function canReset(scope: AgentModesScope, view: AgentModeView): boolean {
  return view.source === scope.kind;
}
